/**
 * Entry point for the EVON login 3D scene.
 *
 * Wired up via the Laravel Vite plugin from vite.config.ts (input list).
 * The login.blade.php view loads this via @vite('resources/js/auth/login3d.ts').
 *
 * Slice 4 — also binds form events to the scene's interactive surface:
 *   - focusin / focusout on inputs → handle.setFocused(true/false)
 *   - Server-rendered error visible on page load → handle.flashError() once
 *
 * The canvas is expected at #model3d. The form is expected at #loginForm.
 * Optional model URL on the canvas: data-model-url="/models/charging-point.glb".
 */

import type { LoginSceneHandle } from '../three/index.js';

const CANVAS_ID = 'model3d';
const FORM_ID = 'loginForm';

function start(): void {
  const canvas = document.getElementById(CANVAS_ID);
  if (!(canvas instanceof HTMLCanvasElement)) {
    console.warn(`[login3d] no <canvas id="${CANVAS_ID}"> found, skipping`);
    return;
  }

  const modelUrl = canvas.dataset['modelUrl'] || null;
  const glassAnchor = document.querySelector<HTMLElement>('.login-card');

  // Dynamic import keeps the Three.js + GSAP bundles off the critical path of
  // the login form. The form is fully interactive before this resolves.
  void import('../three/index.js').then(({ mountLoginScene }) => {
    let handle: LoginSceneHandle | null = null;
    try {
      handle = mountLoginScene(canvas, { modelUrl, glassAnchor });
    } catch (err) {
      console.error('[login3d] scene mount failed:', err);
      return;
    }

    bindFormEvents(handle);
    console.info('[login3d] tier =', handle.tier);

    Object.defineProperty(window, '__evonLoginScene', {
      value: handle,
      configurable: true,
      writable: true,
    });
  }).catch((err: unknown) => {
    console.error('[login3d] module import failed:', err);
  });
}

function bindFormEvents(handle: LoginSceneHandle): void {
  const form = document.getElementById(FORM_ID);
  if (!(form instanceof HTMLFormElement)) {
    // The scene still mounts and parallaxes; we just don't get focus-driven
    // emphasis or the error flash. Quiet warning, not an error.
    console.info(`[login3d] no <form id="${FORM_ID}"> — focus interactions disabled`);
    return;
  }

  form.addEventListener('focusin', (e) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      handle.setFocused(true);
    }
  });

  form.addEventListener('focusout', (e) => {
    if (!(e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement)) return;
    // Wait one frame so we can tell whether focus moved to another field in
    // the same form (stay focused) vs. left entirely (drop focus).
    requestAnimationFrame(() => {
      if (!form.contains(document.activeElement) || document.activeElement === document.body) {
        handle.setFocused(false);
      }
    });
  });

  // Server-rendered validation errors → emissive red flash. Wait a beat so
  // the emissive has settled to its base intensity before the flash hits.
  if (form.querySelector('.error')) {
    setTimeout(() => handle.flashError(), 500);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
